import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useTriggerDetailsFullSync, useTriggerLinesFullSync } from '@/hooks/useSyncMonitoringActions';
import type { SyncOverview } from '@octave/contracts';
import { formatRelative, type SyncHealth } from '@/lib/sync-monitoring';
import { cn } from '@/lib/utils';
import {
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  XCircle,
} from 'lucide-react';

const STYLES = {
  ok: {
    container: 'border-emerald-200 bg-emerald-50',
    title: 'text-emerald-900',
    subtitle: 'text-emerald-700',
    Icon: CheckCircle2,
    icon: 'text-emerald-600',
  },
  degraded: {
    container: 'border-amber-200 bg-amber-50',
    title: 'text-amber-900',
    subtitle: 'text-amber-700',
    Icon: AlertTriangle,
    icon: 'text-amber-600',
  },
  broken: {
    container: 'border-rose-200 bg-rose-50',
    title: 'text-rose-900',
    subtitle: 'text-rose-700',
    Icon: XCircle,
    icon: 'text-rose-600',
  },
} as const;

const TITLES = {
  ok: 'Tout est synchronisé',
  degraded: 'Synchronisation à surveiller',
  broken: 'Synchronisation en échec',
} as const;

interface SyncHealthBannerProps {
  overview: SyncOverview | undefined;
  health: SyncHealth | null;
  loading: boolean;
  error: boolean;
}

export function SyncHealthBanner({
  overview,
  health,
  loading,
  error,
}: SyncHealthBannerProps) {
  const triggerDetailsFullSync = useTriggerDetailsFullSync();
  const triggerLinesFullSync = useTriggerLinesFullSync();

  if (loading) {
    return <Skeleton className="h-20 w-full rounded-xl" />;
  }

  if (error || !overview || !health) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 px-5 py-4 text-sm text-rose-700">
        Erreur lors du chargement de l'état de synchronisation.
      </div>
    );
  }

  const style = STYLES[health.level];
  const { Icon } = style;

  const lastRunAt = [
    overview.lastRuns.lines?.startedAt,
    overview.lastRuns.organizationDetails?.startedAt,
  ]
    .filter((d): d is string => !!d)
    .sort()
    .at(-1);

  const subtitle =
    health.level === 'ok'
      ? `Dernier sync ${formatRelative(lastRunAt ?? null)} · ${overview.linkedOrganizations} organisation(s) · ${overview.lines.totalLines} ligne(s)`
      : health.reasons.join(' · ');

  return (
    <div
      className={cn(
        'flex flex-col gap-3 rounded-xl border px-5 py-4 sm:flex-row sm:items-center sm:justify-between',
        style.container
      )}
    >
      <div className="flex items-start gap-3">
        <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', style.icon)} />
        <div>
          <p className={cn('text-sm font-semibold', style.title)}>
            {TITLES[health.level]}
          </p>
          <p className={cn('mt-0.5 text-xs', style.subtitle)}>{subtitle}</p>
        </div>
      </div>
      <div className="flex shrink-0 gap-2">
        <Button
          size="sm"
          variant="ghost"
          className={style.title}
          onClick={() => triggerDetailsFullSync.mutate()}
          disabled={triggerDetailsFullSync.isPending}
        >
          <RefreshCw
            className={cn(
              'mr-1.5 h-3.5 w-3.5',
              triggerDetailsFullSync.isPending && 'animate-spin'
            )}
          />
          Sync orgs & sites
        </Button>
        <Button
          size="sm"
          variant="ghost"
          className={style.title}
          onClick={() => triggerLinesFullSync.mutate()}
          disabled={triggerLinesFullSync.isPending}
        >
          <RefreshCw
            className={cn(
              'mr-1.5 h-3.5 w-3.5',
              triggerLinesFullSync.isPending && 'animate-spin'
            )}
          />
          Sync lignes
        </Button>
      </div>
    </div>
  );
}
